
export type Environment = 'development' | 'staging' | 'production';

// Global variable to store the initial editor detection result
// This ensures consistency throughout the application lifecycle
let globalEditorDetection: boolean | null = null;

// Force variable that always overrides detection logic if set
let forcedEditorMode: boolean | null = null;

// Added debug flag to enable additional console logs
const ENABLE_ENV_DEBUG = true;

/**
 * Get the current application environment based on hostname
 */
export const getEnvironment = (): Environment => {
  const host = window.location.hostname;
  
  // Debug logging
  if (ENABLE_ENV_DEBUG) {
    console.log(`[Environment Debug] Current hostname: ${host}`);
    console.log(`[Environment Debug] NODE_ENV: ${process.env.NODE_ENV}`);
    console.log(`[Environment Debug] Window location:`, {
      hostname: window.location.hostname,
      pathname: window.location.pathname,
      href: window.location.href,
      protocol: window.location.protocol
    });
  }

  if (host.includes('lamulamu.com')) {
    return 'production';
  } else if (host.includes('lovable.app')) {
    return 'staging';
  }
  
  return 'development';
};

// Helper to check if we're in production - Relies SOLELY on URL patterns, ignoring NODE_ENV
export const isProduction = (): boolean => {
  // First check for URL parameters that can force an environment
  const urlParams = new URLSearchParams(window.location.search);
  const forceProduction = urlParams.get('force-production') === 'true';
  const forceDevelopment = urlParams.get('force-development') === 'true';
  
  if (forceDevelopment) {
    if (ENABLE_ENV_DEBUG) {
      console.log('[Environment Debug] Forcing development mode via URL parameter');
    }
    return false;
  }
  
  if (forceProduction) {
    if (ENABLE_ENV_DEBUG) {
      console.log('[Environment Debug] Forcing production mode via URL parameter');
    }
    return true;
  }
  
  // Get hostname and full URL for pattern matching
  const host = window.location.hostname;
  const url = window.location.href;
  
  // Debug logging
  if (ENABLE_ENV_DEBUG) {
    console.log(`[Environment Debug] isProduction check - host: ${host}`);
    console.log(`[Environment Debug] isProduction check - url: ${url}`);
    console.log(`[Environment Debug] isProduction check - NODE_ENV: ${process.env.NODE_ENV}`);
  }
  
  // PRODUCTION DOMAINS - explicitly list known production patterns
  const productionPatterns = [
    'lamulamu.com',
  ];
  
  // Special case for lamulamu-*.lovable.app (production) but not preview--lamulamu-*.lovable.app
  const isLamulamuProduction = host.includes('lamulamu-') && 
                              host.includes('lovable.app') && 
                              !host.includes('preview--');
  
  // Check if URL matches any production pattern
  const isProductionDomain = productionPatterns.some(pattern => 
    host.includes(pattern) || url.includes(pattern)
  ) || isLamulamuProduction;
  
  // NON-PRODUCTION URLs (development environments)
  const nonProductionPatterns = [
    'localhost',
    '127.0.0.1',
    'lovable.dev',
    'preview--',
    'lovableproject.com'
  ];
  
  // Check if URL contains any non-production pattern
  const isNonProductionUrl = nonProductionPatterns.some(pattern => 
    host.includes(pattern) || url.includes(pattern)
  );
  
  // Final determination - prioritizing URL patterns over NODE_ENV
  // If it's explicitly a production domain, it's production regardless of NODE_ENV
  // If it matches a non-production pattern, it's definitely not production
  // Only fall back to NODE_ENV if we can't determine from the URL
  const isProd = isProductionDomain || (!isNonProductionUrl && process.env.NODE_ENV === 'production');
  
  if (ENABLE_ENV_DEBUG) {
    console.log(`[Environment Debug] Final production determination: ${isProd}`);
    console.log(`[Environment Debug] Reasons: NodeEnv=${process.env.NODE_ENV}, NonProductionUrl=${isNonProductionUrl}, ProductionDomain=${isProductionDomain}`);
  }
  
  return isProd;
};

/**
 * Initialize environment detection once and store it globally
 * Called as early as possible in the application lifecycle
 */
export const initializeEnvironmentDetection = (): boolean => {
  console.debug('[Environment] Initializing environment detection...');
  
  // If force variable is set, always use it (highest priority)
  if (forcedEditorMode !== null) {
    console.log('[Environment] Using forced editor mode:', forcedEditorMode);
    
    // Store the forced value everywhere for consistency
    storeEditorDetection(forcedEditorMode);
    globalEditorDetection = forcedEditorMode;
    return forcedEditorMode;
  }
  
  // If already initialized, return cached result
  if (globalEditorDetection !== null) {
    console.log('[Environment] Using cached global detection:', globalEditorDetection);
    return globalEditorDetection;
  }

  // Try to get from storage first (most reliable across refreshes)
  try {
    const storedLocalResult = localStorage.getItem('lamulamu_is_lovable_editor');
    const storedSessionResult = sessionStorage.getItem('lamulamu_is_lovable_editor');
    
    // If both storage types exist and agree, use that
    if (storedLocalResult && storedSessionResult && 
        storedLocalResult === storedSessionResult) {
      const result = storedLocalResult === 'true';
      console.log('[Environment] Using consistent stored detection result:', result, 
        '(localStorage and sessionStorage match)');
      globalEditorDetection = result;
      
      // Update DOM attributes for CSS targeting
      if (result) {
        document.documentElement.dataset.editor = 'lovable';
        document.documentElement.classList.add('lovable-editor');
      }
      
      return result;
    }
    
    // If they don't match, prefer localStorage as it persists across tabs
    if (storedLocalResult) {
      const result = storedLocalResult === 'true';
      console.log('[Environment] Using localStorage detection result:', result, 
        '(storage values were inconsistent)');
      globalEditorDetection = result;
      
      // Make sure sessionStorage matches
      sessionStorage.setItem('lamulamu_is_lovable_editor', storedLocalResult);
      
      // Update DOM attributes
      if (result) {
        document.documentElement.dataset.editor = 'lovable';
        document.documentElement.classList.add('lovable-editor');
      }
      
      return result;
    }
  } catch (e) {
    console.warn('[Environment] Storage error during detection:', e);
  }

  // Perform detection and store result
  const result = detectLovableEditorEnvironment();
  console.log('[Environment] Performed full detection with result:', result);
  
  globalEditorDetection = result;
  
  // Store in both storage types for redundancy
  storeEditorDetection(result);
  
  return result;
};

/**
 * Core detection logic - improved for maximum reliability
 */
const detectLovableEditorEnvironment = (): boolean => {
  console.log('[Environment] Performing enhanced environment detection...');
  
  // STEP 1: DOM attributes (most reliable, set by editor scripts)
  const hasEditorAttribute = document.documentElement.dataset.editor === 'lovable';
  const hasEditorClass = document.documentElement.classList.contains('lovable-editor');
  
  if (hasEditorAttribute || hasEditorClass) {
    console.log('[Environment] Detected via DOM attributes:', { hasEditorAttribute, hasEditorClass });
    return true;
  }
  
  // STEP 2: Window properties (reliable, set by Lovable's editor)
  if ((window as any).__LOVABLE_IS_EDITOR === true) {
    console.log('[Environment] Detected via window.__LOVABLE_IS_EDITOR property');
    return true;
  }
  
  // STEP 3: URL patterns (reliable - checks all possible Lovable editor URLs)
  const url = window.location.href;
  const isLovableUrl = 
    url.includes('lovable.dev') || 
    url.includes('lovableproject.com') || 
    url.includes('id-preview') ||
    url.includes('lovable.app') && 
    (url.includes('/editor/') || url.includes('/preview/'));
  
  // STEP 4: Query parameters (can be explicitly set)
  const urlParams = new URLSearchParams(window.location.search);
  const hasEditorParam = urlParams.has('lovable-editor');
  
  // STEP 5: Enhanced iframe detection (crucial for editor environment)
  // We use a multi-layered approach that's less likely to give false positives
  const inIframe = (() => {
    try {
      // Check if window.self and window.top are different
      if (window.self !== window.top) return true;
      
      // Also check if we're in any kind of frame
      if (window.frameElement !== null) return true;
      
      // Check parent relationship
      if (window.parent !== window) return true;
      
      return false;
    } catch (e) {
      // If we get a security error, we're definitely in an iframe
      return true;
    }
  })();
  
  // COMBINE CHECKS: For best accuracy, require both iframe AND a Lovable URL pattern
  const result = (inIframe && (isLovableUrl || hasEditorParam));
  
  console.log('[Environment] Enhanced detection components:', { 
    inIframe, 
    isLovableUrl,
    url,
    hasEditorParam,
    result,
    detection: 'enhanced'
  });
  
  // Set high resolution timestamp of last detection
  try {
    sessionStorage.setItem('lamulamu_last_detection_time', String(performance.now()));
  } catch (e) {
    // Ignore storage errors
  }
  
  return result;
};

/**
 * Force specific editor mode - for debugging and fixing environment issues
 * This will override ALL other detection methods
 */
export const forceEditorMode = (isEditor: boolean): void => {
  forcedEditorMode = isEditor;
  console.log('[Environment] FORCING EDITOR MODE TO:', isEditor);
  
  // Update global variable
  globalEditorDetection = isEditor;
  
  // Store in storage
  storeEditorDetection(isEditor);
  
  // Apply DOM changes
  applyEditorDOMChanges(isEditor);
};

/**
 * Apply editor DOM attributes for consistent CSS targeting
 */
export const applyEditorDOMChanges = (isEditor: boolean): void => {
  if (isEditor) {
    document.documentElement.dataset.editor = 'lovable';
    document.documentElement.classList.add('lovable-editor');
    document.body.classList.add('lovable-editor-body');
  } else {
    delete document.documentElement.dataset.editor;
    document.documentElement.classList.remove('lovable-editor');
    document.body.classList.remove('lovable-editor-body');
  }
  
  console.log('[Environment] Applied DOM changes for editor mode:', isEditor);
};

/**
 * Consistent detection for Lovable editor environment
 * Always uses the initialized value for consistency
 */
export const isLovableEditor = (): boolean => {
  // If force is set, always use that
  if (forcedEditorMode !== null) {
    return forcedEditorMode;
  }
  
  // Check if we've already determined this through initialization
  if (globalEditorDetection !== null) {
    return globalEditorDetection;
  }
  
  // If not initialized yet, initialize now
  return initializeEnvironmentDetection();
};

/**
 * Store editor detection in both localStorage and sessionStorage
 * for maximum resilience across refreshes and HMR updates
 */
export function storeEditorDetection(isEditor: boolean): void {
  try {
    // Store in both storage types for redundancy
    localStorage.setItem('lamulamu_is_lovable_editor', isEditor ? 'true' : 'false');
    sessionStorage.setItem('lamulamu_is_lovable_editor', isEditor ? 'true' : 'false');
    
    // Also set timestamp for debugging
    const timestamp = new Date().toISOString();
    localStorage.setItem('lamulamu_editor_detection_time', timestamp);
    sessionStorage.setItem('lamulamu_editor_detection_time', timestamp);
    
    console.log('[Environment] Editor detection state stored:', isEditor);
  } catch (e) {
    // Ignore storage errors
    console.warn('[Environment] Could not store editor detection state:', e);
  }
}

// Helper to detect if we should use simplified rendering for better editor compatibility
export const useSafeRendering = (): boolean => {
  const result = isLovableEditor() || window.location.search.includes('safe-mode');
  return result;
};

// Helper to get base URL that works in both Lovable editor and normal environments
export const getBaseUrl = (): string => {
  if (isLovableEditor()) {
    // For Lovable editor, use absolute paths
    return '/';
  }
  
  // For normal environments, use relative paths
  return './';
};

/**
 * Enable recovery mode when needed
 */
export const enableRecoveryMode = () => {
  try {
    sessionStorage.setItem('lamulamu_recovery_mode', 'true');
    console.log('Recovery mode enabled');
  } catch (e) {
    // Ignore storage errors
  }
};

/**
 * Check if recovery mode is enabled
 */
export const isRecoveryMode = (): boolean => {
  return sessionStorage.getItem('lamulamu_recovery_mode') === 'true';
};

/**
 * Reset recovery mode
 */
export const resetRecoveryMode = () => {
  try {
    sessionStorage.removeItem('lamulamu_recovery_mode');
    console.log('Recovery mode reset');
  } catch (e) {
    // Ignore storage errors
  }
};

/**
 * Debug helper to check if environment detection has changed during runtime
 */
export const checkEnvironmentDetectionConsistency = (): void => {
  // Compare global variable with storage
  const storedSession = sessionStorage.getItem('lamulamu_is_lovable_editor');
  const storedLocal = localStorage.getItem('lamulamu_is_lovable_editor');
  
  const sessionValue = storedSession === 'true';
  const localValue = storedLocal === 'true';
  
  if (storedSession !== storedLocal) {
    console.warn('[Environment] Inconsistent storage detection!', {
      sessionStorage: sessionValue,
      localStorage: localValue,
      global: globalEditorDetection
    });
  }
  
  if (globalEditorDetection !== null && storedSession !== null && 
      globalEditorDetection !== sessionValue) {
    console.warn('[Environment] Global detection differs from storage!', {
      global: globalEditorDetection,
      stored: sessionValue
    });
    
    // Restore from global (source of truth)
    storeEditorDetection(globalEditorDetection);
  }
};

/**
 * Force update of environment detection - use with caution
 * This should only be used in controlled scenarios like manual refresh
 */
export const refreshEnvironmentDetection = (): boolean => {
  // If forced mode is active, don't change it
  if (forcedEditorMode !== null) {
    console.log('[Environment] Skipping refresh since forced mode is active:', forcedEditorMode);
    return forcedEditorMode;
  }
  
  // Log the call stack to help debugging where refreshes are coming from
  console.log('[Environment] Refresh detection called from:', new Error().stack);
  
  // Clear the cached result
  globalEditorDetection = null;
  
  // Re-detect environment (will update global)
  const result = initializeEnvironmentDetection();
  console.log(`[Environment] Refreshed editor detection: ${result}`);
  
  return result;
};

/**
 * Special debug function to force editor state
 * ONLY for debugging purposes
 */
export const _DEBUG_forceEditorState = (isEditor: boolean): void => {
  console.log(`[DEBUG] Forcing editor state to: ${isEditor}`);
  forcedEditorMode = isEditor; // Set the override flag
  globalEditorDetection = isEditor;
  storeEditorDetection(isEditor);
  applyEditorDOMChanges(isEditor);
  
  // Update window properties for other components
  (window as any).__LOVABLE_IS_EDITOR = isEditor;
  (window as any).__LOVABLE_EDITOR_DETECTION = isEditor;
  
  // Set timestamp to help with debugging
  (window as any).__LOVABLE_EDITOR_FORCED_TIME = Date.now();
};

// Export global detection for debugging
(window as any).__LOVABLE_EDITOR_DETECTION = globalEditorDetection;

// Called during import to set initial state
(() => {
  console.log('[Environment] Module initialized');
})();
